// =====================================================
// 📁 POSCreditQuantityModal.tsx
// =====================================================

import { useEffect, useMemo, useState } from 'react';

import FormModal from '../../../../Modals/FormModal';

import POSInputField from '../../../../POS/Fields/POSInputField';

import POSActionButton from '../shared/POSActionButton';

interface Props {
  isOpen: boolean;

  onClose: () => void;

  onConfirm: (quantity: number) => void;

  product: {
    productId: number;

    productName: string;

    quantity: number;

    unitPrice: number;

    returnedQuantity?: number;
  };
}

const POSCreditQuantityModal: React.FC<Props> = ({
  isOpen,

  onClose,

  onConfirm,

  product,
}) => {
  // =====================================================
  // 🔹 Available
  // =====================================================
  const alreadyReturned = Number(product.returnedQuantity || 0);

  const availableQuantity = Number(product.quantity || 0) - alreadyReturned;

  // =====================================================
  // 🔹 States
  // =====================================================
  const [quantity, setQuantity] = useState<number>(1);

  // =====================================================
  // 🔹 Reset
  // =====================================================
  useEffect(() => {
    if (isOpen) {
      setQuantity(availableQuantity > 0 ? 1 : 0);
    }
  }, [isOpen, availableQuantity]);

  // =====================================================
  // 🔹 Total
  // =====================================================
  const estimatedTotal = useMemo(() => {
    return Number(product.unitPrice || 0) * quantity;
  }, [product.unitPrice, quantity]);

  // =====================================================
  // 🔹 Handlers
  // =====================================================
  const handleConfirm = () => {
    if (quantity <= 0 || quantity > availableQuantity) {
      return;
    }

    onConfirm(quantity);

    onClose();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Agregar devolución"
      maxWidth="max-w-md"
    >
      <div className="space-y-5">
        {/* ===================================================== */}
        {/* 🔹 Product Info */}
        {/* ===================================================== */}
        <div
          className="
            bg-gray-1

            rounded-2xl
            border

            border-stroke

            p-4

            dark:border-strokedark
            dark:bg-meta-4
          "
        >
          {/* 🔹 Product */}
          <div>
            <h3
              className="
                text-sm
                font-semibold
                text-black

                dark:text-white
              "
            >
              {product.productName}
            </h3>
          </div>

          {/* 🔹 Meta */}
          <div
            className="
              mt-4

              grid
              grid-cols-2
              gap-3
            "
          >
            {/* Precio */}
            <div
              className="
                rounded-xl

                bg-white

                px-3
                py-2

                dark:bg-boxdark
              "
            >
              <p
                className="
                  text-[11px]
                  font-medium
                  uppercase
                  tracking-wide
                  text-body
                "
              >
                Precio
              </p>

              <p
                className="
                  mt-1

                  text-sm
                  font-semibold
                  text-primary
                "
              >
                ${Number(product.unitPrice).toFixed(2)}
              </p>
            </div>

            {/* Disponible */}
            <div
              className="
                rounded-xl

                bg-white

                px-3
                py-2

                dark:bg-boxdark
              "
            >
              <p
                className="
                  text-[11px]
                  font-medium
                  uppercase
                  tracking-wide
                  text-body
                "
              >
                Disponible
              </p>

              <p
                className="
                  mt-1

                  text-sm
                  font-semibold
                  text-[#3CA745]
                "
              >
                {availableQuantity}
              </p>
            </div>
          </div>
        </div>

        {/* ===================================================== */}
        {/* 🔹 Quantity */}
        {/* ===================================================== */}
        <POSInputField
          name={`credit-quantity-${product.productId}`}
          label="Cantidad a devolver"
          type="number"
          min={1}
          max={availableQuantity}
          placeholder="Ej: 1"
          icon="hash"
          value={quantity || ''}
          onChange={(e) => {
            const value = e.target.value.trim();

            if (value === '') {
              setQuantity(0);
              return;
            }

            const parsed = Number(value);

            if (isNaN(parsed)) {
              return;
            }

            if (parsed < 1) {
              setQuantity(1);
              return;
            }

            if (parsed > availableQuantity) {
              setQuantity(availableQuantity);

              return;
            }

            setQuantity(parsed);
          }}
        />

        {/* ===================================================== */}
        {/* 🔹 Summary */}
        {/* ===================================================== */}
        <div
          className="
            rounded-2xl

            border
            border-primary/20

            bg-primary/5

            px-4
            py-3
          "
        >
          <div
            className="
              flex
              items-center
              justify-between
            "
          >
            <span
              className="
                text-sm
                text-body
              "
            >
              Total devolución
            </span>

            <span
              className="
                text-lg
                font-bold
                text-primary
              "
            >
              ${estimatedTotal.toFixed(2)}
            </span>
          </div>
        </div>

        {/* ===================================================== */}
        {/* 🔹 Actions */}
        {/* ===================================================== */}
        <div
          className="
            flex
            items-center
            justify-end
            gap-2
          "
        >
          <POSActionButton variant="secondary" onClick={onClose}>
            Cancelar
          </POSActionButton>

          <POSActionButton
            variant="primary"
            disabled={quantity <= 0 || quantity > availableQuantity}
            onClick={handleConfirm}
          >
            Confirmar devolución
          </POSActionButton>
        </div>
      </div>
    </FormModal>
  );
};

export default POSCreditQuantityModal;
